import React, { useState } from 'react'
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material'
import { FileUpload as ImportIcon } from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from '@hooks/redux'
import { createHive, fetchApiariesAndGroups } from '@store/slices/locationsSlice'

const parseCsv = (text: string): string[][] =>
  text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => line.split(',').map((cell) => cell.trim().replace(/^"|"$/g, '')))

const ImportData: React.FC = () => {
  const dispatch = useAppDispatch()
  const { apiaries } = useAppSelector((state) => state.locations)
  const [apiaryId, setApiaryId] = useState<number | ''>(apiaries[0]?.id || '')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !apiaryId) {
      setMessage({ type: 'error', text: 'Choose an apiary and a CSV file first.' })
      return
    }

    setLoading(true)
    setMessage(null)
    try {
      const text = await file.text()
      const rows = parseCsv(text)
      if (rows.length < 2) {
        throw new Error('CSV needs a header row and at least one hive row.')
      }

      const header = rows[0].map((cell) => cell.toLowerCase())
      const nameIndex = header.findIndex((cell) => cell === 'name' || cell === 'hive' || cell === 'hive_name')
      if (nameIndex === -1) {
        throw new Error('CSV must include a name column.')
      }
      const notesIndex = header.findIndex((cell) => cell === 'notes' || cell === 'note')
      const raceIndex = header.findIndex((cell) => cell === 'bee_race' || cell === 'race')

      let created = 0
      for (const row of rows.slice(1)) {
        const name = row[nameIndex]
        if (!name) continue
        await dispatch(
          createHive({
            name,
            location_id: Number(apiaryId),
            notes: notesIndex >= 0 ? row[notesIndex] : '',
            bee_race: raceIndex >= 0 ? row[raceIndex] : 'african',
            color: '#F8B133',
          } as any),
        ).unwrap()
        created += 1
      }

      await dispatch(fetchApiariesAndGroups())
      setMessage({
        type: 'success',
        text: created ? `Imported ${created} hive${created === 1 ? '' : 's'}.` : 'No hive rows were imported.',
      })
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to import CSV.' })
    } finally {
      setLoading(false)
      event.target.value = ''
    }
  }

  return (
    <Container sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Import data
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Add hives from a CSV file. Required column: <code>name</code>. Optional: <code>notes</code>, <code>bee_race</code>.
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Apiary</InputLabel>
          <Select
            value={apiaryId}
            label="Apiary"
            onChange={(event) => setApiaryId(Number(event.target.value))}
          >
            {apiaries.map((apiary) => (
              <MenuItem key={apiary.id} value={apiary.id}>
                {apiary.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          component="label"
          startIcon={loading ? <CircularProgress size={18} /> : <ImportIcon />}
          disabled={loading || !apiaryId}
        >
          Upload hive CSV
          <input hidden type="file" accept=".csv,text/csv" onChange={handleFile} />
        </Button>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Example
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, fontFamily: 'monospace', fontSize: 13 }}>
            name,notes,bee_race<br />
            Hive NYK-002,Langstroth at the east fence,african<br />
            Hive NYK-003,Top bar near the mango tree,african
          </Paper>
        </Box>
      </Paper>
    </Container>
  )
}

export default ImportData
